import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";

// ✅ Lazy loading de componentes de UI pesados
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));
const AppLayout = lazy(() => import("./components/layout/AppLayout").then(m => ({ default: m.AppLayout })));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const AdminProtectedRoute = lazy(() => import("./components/AdminProtectedRoute"));

// Skeleton simples inline para evitar imports
const MinimalSkeleton = () => (
  <div className="min-h-screen bg-background animate-pulse" />
);

// ✅ Lazy Loading de todas as páginas
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardMentor = lazy(() => import("./pages/DashboardMentor"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MentoriaDetalhes = lazy(() => import("./pages/MentoriaDetalhes"));
const Agenda = lazy(() => import("./pages/Agenda"));
const AgendaCalendario = lazy(() => import("./pages/agenda/AgendaCalendario"));
const AgendaSessoes = lazy(() => import("./pages/agenda/AgendaSessoes"));
const AgendaDisponibilidade = lazy(() => import("./pages/agenda/AgendaDisponibilidade"));
const Perfil = lazy(() => import("./pages/Perfil"));
const PerfilPublicoMentor = lazy(() => import("./pages/PerfilPublicoMentor"));
const MentoresPublicos = lazy(() => import("./pages/MentoresPublicos"));
const BuscarMentores = lazy(() => import("./pages/BuscarMentores"));
const ConfiguracoesLayout = lazy(() => import("./pages/configuracoes/ConfiguracoesLayout").then(m => ({ default: m.ConfiguracoesLayout })));
const Notificacoes = lazy(() => import("./pages/configuracoes/Notificacoes"));
const MentoriasConfig = lazy(() => import("./pages/configuracoes/Mentorias"));
const Usuarios = lazy(() => import("./pages/configuracoes/Usuarios"));
const CRO = lazy(() => import("./pages/configuracoes/CRO"));
const AuditLogs = lazy(() => import("./pages/configuracoes/AuditLogs"));
const DashboardSeguranca = lazy(() => import("./pages/configuracoes/DashboardSeguranca"));
const Creditos = lazy(() => import("./pages/configuracoes/Creditos"));
const Suporte = lazy(() => import("./pages/Suporte"));
const SuporteNovo = lazy(() => import("./pages/SuporteNovo"));
const TicketDetalhes = lazy(() => import("./pages/TicketDetalhes"));
const SuporteAdmin = lazy(() => import("./pages/configuracoes/SuporteAdmin"));
const MentoriasAdmin = lazy(() => import("./pages/configuracoes/MentoriasAdmin"));
const Planos = lazy(() => import("./pages/Planos"));
const CheckoutSucesso = lazy(() => import("./pages/CheckoutSucesso"));
const PlanoSettings = lazy(() => import("./pages/configuracoes/PlanoSettings"));

// ✅ Configuração otimizada do React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Homepage com Planos */}
            <Route path="/" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <Planos />
              </Suspense>
            } />
            {/* Redirect /planos para raiz para compatibilidade */}
            <Route path="/planos" element={<Navigate to="/" replace />} />
            <Route path="/auth" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <Auth />
              </Suspense>
            } />
            <Route path="/reset-password" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <ResetPassword />
              </Suspense>
            } />
            <Route path="/mentores" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <MentoresPublicos />
              </Suspense>
            } />
            <Route path="/mentor/:id" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <PerfilPublicoMentor />
              </Suspense>
            } />
            <Route path="/checkout-sucesso" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <ProtectedRoute>
                  <CheckoutSucesso />
                </ProtectedRoute>
              </Suspense>
            } />
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AppLayout>
                      <Dashboard />
                    </AppLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/dashboard-mentor"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AppLayout>
                      <DashboardMentor />
                    </AppLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/buscar-mentores"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AppLayout>
                      <BuscarMentores />
                    </AppLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/mentorias/:id"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AppLayout>
                      <MentoriaDetalhes />
                    </AppLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/agenda"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AppLayout>
                      <Agenda />
                    </AppLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            >
              <Route
                path="calendario"
                element={
                  <Suspense fallback={<MinimalSkeleton />}>
                    <AgendaCalendario />
                  </Suspense>
                }
              />
              <Route
                path="sessoes"
                element={
                  <Suspense fallback={<MinimalSkeleton />}>
                    <AgendaSessoes />
                  </Suspense>
                }
              />
              <Route
                path="disponibilidade"
                element={
                  <Suspense fallback={<MinimalSkeleton />}>
                    <AgendaDisponibilidade />
                  </Suspense>
                }
              />
            </Route>
            <Route
              path="/perfil"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <Perfil />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/notificacoes"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <ConfiguracoesLayout>
                      <Notificacoes />
                    </ConfiguracoesLayout>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/mentorias"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="mentor">
                      <ConfiguracoesLayout>
                        <MentoriasConfig />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/usuarios"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <ConfiguracoesLayout>
                        <Usuarios />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/cro"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <ConfiguracoesLayout>
                        <CRO />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/audit-logs"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <ConfiguracoesLayout>
                        <AuditLogs />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/dashboard-seguranca"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <ConfiguracoesLayout>
                        <DashboardSeguranca />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/creditos"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <ConfiguracoesLayout>
                        <Creditos />
                      </ConfiguracoesLayout>
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/suporte"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <SuporteAdmin />
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/gestao-mentorias"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <MentoriasAdmin />
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes/planos"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <AdminProtectedRoute requiredRole="superadmin">
                      <PlanoSettings />
                    </AdminProtectedRoute>
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/suporte"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <Suporte />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/suporte/novo"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <SuporteNovo />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/suporte/:id"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <TicketDetalhes />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route
              path="/configuracoes"
              element={
                <Suspense fallback={<MinimalSkeleton />}>
                  <ProtectedRoute>
                    <Navigate to="/configuracoes/notificacoes" replace />
                  </ProtectedRoute>
                </Suspense>
              }
            />
            <Route path="*" element={
              <Suspense fallback={<MinimalSkeleton />}>
                <NotFound />
              </Suspense>
            } />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </Suspense>
  </QueryClientProvider>
);

export default App;
